/**
 * /dashboard — a router, not a page.
 *
 * Sends the caller to the venue they actually belong to. Where that is depends
 * on who they are, and there is no single right answer to hardcode:
 *
 *   one venue      -> straight there
 *   several        -> a chooser, because guessing is worse than asking
 *   none, staff    -> platform administration
 *   none, player   -> a dead end that says so, rather than a redirect loop
 */

import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

const PLATFORM_ROLES = new Set(['platform_owner', 'super_admin']);
const ADMIN_ROLES    = new Set(['venue_admin', 'venue_owner']);

/**
 * Where a role lands inside a venue. A player has no console to open, so
 * sending them to /admin would be a 403 dressed up as a home page.
 */
const homeFor = (venue) =>
  `/venues/${venue.slug}${ADMIN_ROLES.has(venue.role) ? '/admin' : '/my-stats'}`;

export default function Dashboard() {
  const { user } = useAuth();
  const venues = user?.venues ?? [];

  if (venues.length === 1) {
    return <Navigate to={homeFor(venues[0])} replace />;
  }

  if (venues.length === 0) {
    if (PLATFORM_ROLES.has(user?.platformRole)) {
      return <Navigate to="/admin/venues" replace />;
    }

    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">No venue yet</h1>
        <p className="hd-meta">
          Your account is not linked to a venue. Ask a venue admin to add you,
          or register a venue of your own.
        </p>
      </div>
    );
  }

  return (
    <div className="hd-stack">
      <div>
        <h1 className="hd-page-title">Choose a venue</h1>
        <p className="hd-meta">You have a role at {venues.length} venues.</p>
      </div>

      <div className="hd-tiles">
        {venues.map((venue) => (
          <Link key={venue.venue_id} className="hd-tile" to={homeFor(venue)}>
            <div className="hd-tile-value" style={{ fontSize: 18 }}>{venue.name}</div>
            <div className="hd-tile-meta">{venue.role.replace('_', ' ')}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
